import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from '../../../interfaces/company.interface';

interface Props {
  company: Company;
}

export const DocViewCompanyTitle = ({ company }: Props) => {
  const { t } = useTranslation();
  const isBranch = useMemo(() => {
    return Number(company?.branchSerial || 0) > 0;
  }, [company?.branchSerial]);

  const allPhones = useMemo(
    () =>
      [company?.phoneNumber, company?.extraInfo?.phone2, company?.extraInfo?.phone3, company?.extraInfo?.phone4]
        .filter(Boolean)
        .join(', '),
    [company?.phoneNumber, company?.extraInfo],
  );

  const contactLine = useMemo(
    () =>
      [
        allPhones ? `${t('PDF.PHONE_NUMBER')}: ${allPhones}` : null,
        company?.email ? `${t('PDF.EMAIL')}: ${company.email}` : null,
      ]
        .filter(Boolean)
        .join(' - '),
    [allPhones, company?.email, t],
  );

  const getBranchAddress = useCallback(
    (prefix: string, websiteUrl?: string) => {
      const parts = [
        `${prefix}: ${company?.address} ${company?.city} ${company?.postalCode}`,
        websiteUrl ?? null,
      ];
      return (
        <div className="flex-start-start-row-wrap normalText2">
          {parts.filter(Boolean).join(' - ')}
        </div>
      );
    },
    [company?.address, company?.city, company?.postalCode],
  );

  const getMasterAddress = useCallback(() => {
    if (company?.parentCompany) {
      return (
        <div className="flex-start-start-row-wrap normalText2">
          {`${t('PDF.MASTER_COMPANY_TITLE')}: ${company?.parentCompany?.address} ${company?.parentCompany?.city} ${company?.parentCompany?.postalCode}`}
        </div>
      );
    }
  }, [company?.parentCompany, t]);

  const getCompanyAddress = useCallback(() => {
    if (isBranch) {
      return (
        <>
          {getBranchAddress(t('PDF.BRANCH_COMPANY_TITLE'), company?.websiteUrl)}
          {getMasterAddress()}
        </>
      );
    }

    return getBranchAddress(t('PDF.ADDRESS'), company?.websiteUrl);
  }, [getBranchAddress, getMasterAddress, isBranch, t, company?.websiteUrl]);

  return (
    <div className="flex-start-start-column-nowrap">
      <div className="boldText3">{company?.title}</div>
      <div className="truncate-2lines normalText2">{company?.profession}</div>
      <div className="flex-start-start-row-wrap">
        <div className="normalText2">
          {t('PDF.VAT_NUMBER')}: {company?.vatNumber}
          {' - '}
        </div>
        <div className="normalText2">
          {t('PDF.DOY')}: {company?.doy?.name}
        </div>
      </div>
      {getCompanyAddress()}
      {contactLine && <div className="normalText2">{contactLine}</div>}
      <div className="flex-start-start-row-wrap normalText2">
        {company?.gemiInfo && `${t('PDF.GEMI_NUMBER')}: ${company?.gemiInfo}`}
        {company?.menoNumber && ` - ${t('PDF.MENO_NUMBER')}: ${company?.menoNumber}`}
        {company?.eaeNumber && ` - ${t('PDF.EAE_NUMBER')}: ${company?.eaeNumber}`}
      </div>
    </div>
  );
};
